import { shareIdentity } from './householdShare';
import { invalidateRentalTaxReviews } from './rentalTax';
import type { Dossier } from './dueDiligence';
import type { Planning } from './purchasePlanning';
import { setFeeMode } from './purchasePlanning';
// A previous review refers to the old plan. Changes must not inherit it.
export function replacePlanning(dossier: Dossier, next: Planning): Dossier {
  const old = dossier.planning;
  if (
    next.costs.filter((cost) => cost.category === 'fees').length >
      (old?.costs.filter((cost) => cost.category === 'fees').length ?? 0) &&
    next.finance.feeMode !== 'itemized'
  )
    next = setFeeMode(next, 'itemized');
  next = invalidateRentalTaxReviews(old, next);
  const costsChanged =
    JSON.stringify(old?.costs ?? []) !== JSON.stringify(next.costs);
  const unitsChanged =
    JSON.stringify(old?.units ?? []) !== JSON.stringify(next.units);
  const structure = (plan?: Planning) =>
    (plan?.units ?? []).map((unit) => ({
      id: unit.id,
      use: unit.use,
      areaM2: unit.areaM2,
      documentId: unit.documentId,
    }));
  const rentability = (plan?: Planning) =>
    (plan?.units ?? []).map((unit) => ({
      id: unit.id,
      use: unit.use,
      legalRentabilityConfirmed: unit.legalRentabilityConfirmed,
      documentId: unit.documentId,
    }));
  const runningCosts = (plan?: Planning) =>
    (plan?.units ?? []).map((unit) => ({
      id: unit.id,
      ownerCostsMonthly: unit.ownerCostsMonthly,
      maintenanceReserveMonthly: unit.maintenanceReserveMonthly,
      actualDeductibleCostsMonthly: unit.actualDeductibleCostsMonthly,
      operatingCostsMonthly: unit.rentalTax?.operatingCostsMonthly,
      operatingGrowth: unit.rentalTax?.marketGrowthPercent,
    }));
  const structureChanged =
    JSON.stringify(structure(old)) !== JSON.stringify(structure(next));
  const rentabilityChanged =
    JSON.stringify(rentability(old)) !== JSON.stringify(rentability(next));
  const runningCostsChanged =
    JSON.stringify(runningCosts(old)) !== JSON.stringify(runningCosts(next));
  const financeChanged =
    JSON.stringify({ ...old?.finance, budgetReviewed: undefined }) !==
    JSON.stringify({ ...next.finance, budgetReviewed: undefined });
  const requestsChanged =
    JSON.stringify(old?.requests ?? []) !== JSON.stringify(next.requests);
  const resetBudget =
    costsChanged ||
    JSON.stringify(shareIdentity(old?.finance)) !== JSON.stringify(shareIdentity(next.finance)) ||
    structureChanged ||
    runningCostsChanged ||
    old?.finance.purchasePrice !== next.finance.purchasePrice ||
    old?.finance.feeMode !== next.finance.feeMode;
  const keys = new Set<string>();
  if (costsChanged || structureChanged || runningCostsChanged)
    keys.add('costs');
  if (costsChanged || unitsChanged || financeChanged || requestsChanged)
    keys.add('finance');
  if (structureChanged) keys.add('area');
  if (rentabilityChanged) keys.add('alterations');
  const checks = { ...dossier.checks };
  for (const key of keys) {
    if (checks[key])
      checks[key] = { ...checks[key], status: 'open', reviewedAt: '' };
  }
  return {
    ...dossier,
    checks,
    planning: {
      ...next,
      finance: {
        ...next.finance,
        budgetReviewed: resetBudget ? false : next.finance.budgetReviewed,
      },
    },
  };
}
